import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { Updater } from 'use-immer';

import { ApiContext } from '_lib/api/apiContext';

import { Company, CompanyZone } from '_lib/api/apiTypes.shim';
import { Autocomplete, createAutocompleteFilterOptions } from '_lib/components/Autocomplete/Autocomplete';
import { Box } from '_lib/components/Box/Box';
import { TextField } from '_lib/components/TextField/TextField';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';

import { useSelectSisterCompanyDialog } from '_lib/hooks/SelectSisterCompanyDialogHook';
import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';
import { logDebug } from '_lib/utils/logging';

import {
  CompanyContext,
  selectSisterCompanyContext,
  setSisterCompanyContext,
  setZoneZipContext,
  ZoneZipContext
} from '../Company.slice';

export interface AutoSelectOption {
  zipCode: string;
  zone: CompanyZone | null;
  /**
   * Only set for options that explicitly reflect the Zone
   * All other options are for ZipCodes and may optionally have a zone if the company is zoned
   */
  zoneTitle?: string;
}

export type ZoneZipSelectState = {
  disabled: boolean;
  disabledText: string;
  label: string;
  labelHighlight: string;
  search: string;
  searchError: boolean;
  searchResultsOpen: boolean;
  selectedOption: AutoSelectOption | null;
};

export const createZoneZipSelectState = (): ZoneZipSelectState => ({
  disabled: false,
  disabledText: '',
  label: '',
  labelHighlight: '',
  search: '',
  searchError: false,
  searchResultsOpen: false,
  selectedOption: null
});

export type BaseCompanyZoneZipSelectProps = {
  onCompanySwitchConfirmed: (companyId: string) => void;
  companyContext: CompanyContext;
  zoneZipContext: ZoneZipContext | undefined;
  zoneZipSelectState: ZoneZipSelectState;
  setZoneZipSelectState: Updater<ZoneZipSelectState>;
  externalZipOrPostal?: string | null | undefined;
  onSetSelectedOption?: (opt: AutoSelectOption | null) => void;
  onOverrideZipOrPostalYES?: (outOfServiceAreaZip?: string) => void;
  onOverrideZipOrPostalNO?: (outOfServiceAreaZip?: string) => void;
};

export const ZIP_CODE_REGEX = new RegExp(/^[0-9]{5}$/);

/**
 * @returns Matching Zone/ZipCode option if and only if ONE option is an exact match. If no matches are found OR
 * if more than one match is found, the result will be undefined.
 */
export const findZoneZipMatch = (search: string, options: AutoSelectOption[], isZoned: boolean) => {
  if (!search) return undefined;
  if (!isZoned) return options.find((o) => o.zipCode === search);

  const searchLower = search.toLowerCase();
  const matches = options.filter(
    (o) => (!o.zoneTitle && o.zipCode === search) || o.zoneTitle?.toLowerCase() === searchLower
  );
  return matches.length === 1 ? matches[0] : undefined;
};

/**
 * As the user searches for a Zone/ZipCode this filterOption what narrows down the results.
 * - For ZipCode company: results should be filtered by ZipCode lookups
 * - For Zone Mode: Results should show Zone titles OR ZipCodes (w/zone title)
 */
export const zoneZipFilterOptions = createAutocompleteFilterOptions({
  ignoreCase: true,
  stringify: (option: AutoSelectOption) => (option.zoneTitle ? option.zoneTitle : option.zipCode)
});

export const getZoneZipOptions = (company: Company): AutoSelectOption[] => {
  if (!company.isZoned) {
    return company.callCenter.zipCodesServiced.map((zipCode) => ({ zipCode, zone: null }));
  }

  return company.zones.reduce((acc, zone) => {
    const firstZipCode = zone.zipCodes.length && (zone.zipCodes[0] as string);
    if (firstZipCode) {
      acc.push({ zipCode: firstZipCode, zone, zoneTitle: zone.title });
      zone.zipCodes.forEach((zipCode) => acc.push({ zipCode, zone }));
    }
    return acc;
  }, [] as AutoSelectOption[]);
};

export const BaseCompanyZoneZipSelect = (props: BaseCompanyZoneZipSelectProps): React.ReactElement => {
  const {
    onCompanySwitchConfirmed,
    companyContext,
    zoneZipContext,
    externalZipOrPostal,
    zoneZipSelectState,
    setZoneZipSelectState,
    onSetSelectedOption,
    onOverrideZipOrPostalYES,
    onOverrideZipOrPostalNO
  } = props;

  const api = useContext(ApiContext);

  const dispatch = useAppDispatch();

  const sisterCompanyContext = useAppSelector(selectSisterCompanyContext);

  const { company } = companyContext;
  const companyIdRef = useRef(company.id);
  const externalZipOrPostalRef = useRef(externalZipOrPostal);

  const confirmationDialog = useConfirmationDialog();
  const alertDialog = useAlertDialog();
  const selectSisterCompanyDialog = useSelectSisterCompanyDialog();

  const zoneZipOptions = useMemo<AutoSelectOption[]>(() => {
    return getZoneZipOptions(company);
    // company.id is sufficient (for now) as we only need to re-initialize zoneZipOptions when company changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id]);

  /**
   * Primary setter for the selectedOption since we always
   * close the search results when the value is set
   */
  const setSelectedOption = useCallback(
    (opt: AutoSelectOption | null) => {
      setZoneZipSelectState((state) => {
        state.selectedOption = opt;
        state.searchResultsOpen = opt ? false : state.searchResultsOpen;
      });

      // Allow setting of companyContext to be overridden via `onSetSelectedOption`
      if (onSetSelectedOption) {
        onSetSelectedOption(opt);
      } else {
        const selectedZone = opt?.zone || null;
        const selectedZipOrPostal = opt?.zipCode || null;
        if (
          zoneZipContext?.selectedZone?.id !== selectedZone?.id ||
          zoneZipContext?.selectedZipOrPostal !== selectedZipOrPostal
        ) {
          dispatch(setZoneZipContext({ selectedZone, selectedZipOrPostal, overrideZipMatch: null }));
        }
      }
    },
    [zoneZipContext, dispatch, onSetSelectedOption, setZoneZipSelectState]
  );

  const handleOutOfServiceAreaZip = useCallback(
    async (zipOrPostal: string) => {
      setSelectedOption(null);

      let sisterCompanies = sisterCompanyContext?.sisterCompanies;
      if ((!sisterCompanies || !sisterCompanies.length) && company.sisterCompanies.length) {
        sisterCompanies = await Promise.all(
          company.sisterCompanies.map((sisterCompany) => api.companies.getById({ id: sisterCompany.id }))
        );
        dispatch(setSisterCompanyContext({ sisterCompanies }));
      }

      const filteredSisterCompanies =
        sisterCompanies?.filter((sisterCompany) => {
          // Never going to match if we don't have a zip/postal code.
          if (!zipOrPostal) return false;

          // No match if there are no trades in common.
          const hasCommonTrade = sisterCompany.callCenter.trades.some((trade) =>
            company.callCenter.trades.includes(trade)
          );
          if (!hasCommonTrade) return false;

          // Check the zip/postal code against the service area.
          let zipOrPostalCodes: string[] = [];
          if (sisterCompany.isZoned) {
            sisterCompany.zones.forEach((zone) => {
              zipOrPostalCodes = zipOrPostalCodes.concat(zone.zipCodes);
            });
          } else {
            zipOrPostalCodes = sisterCompany.callCenter.zipCodesServiced;
          }
          return zipOrPostalCodes.includes(zipOrPostal);
        }) || [];

      // Zip Code Not Listed Confirmation with option to switch to "matching" sister company
      if (filteredSisterCompanies.length > 0) {
        const moreThanOneSisterCompany = filteredSisterCompanies.length > 1;
        const singleSister = filteredSisterCompanies.length === 1 ? filteredSisterCompanies[0] : null;

        const textLines: string[] = [];

        const startOfLine1 = moreThanOneSisterCompany
          ? 'Other companies found that service'
          : 'Another company found that services';
        textLines.push(`${startOfLine1} the zip code: ${zipOrPostal}`);

        if (singleSister) textLines.push(singleSister.name);

        textLines.push(`Do you want to switch to ${moreThanOneSisterCompany ? 'another' : 'the other'} company?`);

        const sisterSwitchConfirmPromise = await confirmationDialog.show({
          titleText: 'ZIP CODE NOT LISTED FOR COMPANY',
          contentText: textLines,
          cancelButtonText: 'No',
          confirmButtonText: 'Yes'
        });

        if (sisterSwitchConfirmPromise?.action === 'CONFIRM' && singleSister) {
          onCompanySwitchConfirmed(singleSister.id);
          return;
        } else if (sisterSwitchConfirmPromise?.action === 'CONFIRM') {
          const sisterSelectedPromise = await selectSisterCompanyDialog.show({
            sisterCompanies: filteredSisterCompanies
          });
          if (sisterSelectedPromise?.action === 'CONFIRM') {
            onCompanySwitchConfirmed(sisterSelectedPromise.value.id);
            return;
          }
        }
      }

      if (company.isZoned) {
        // Warning for Zoned Company and unserviceable zip code
        await alertDialog.show({
          variant: 'warning',
          titleText: 'ZIP CODE NOT IN ZONE',
          content: [company.callCenter.serviceAreaNotes || '', 'Zip code does not match any zone.']
        });
      } else {
        // Confirmation for Non-Zoned company when zip code is not in serviceable zip code
        const overrideZipOrPostalPromise = await confirmationDialog.show({
          titleText: 'ZIP CODE NOT LISTED FOR COMPANY',
          contentText: [company.callCenter.serviceAreaNotes || '', 'Do you want to override the Zip Code match?'],
          cancelButtonText: 'No',
          confirmButtonText: 'Yes'
        });

        if (overrideZipOrPostalPromise?.action === 'CONFIRM') {
          if (onOverrideZipOrPostalYES) {
            onOverrideZipOrPostalYES(zipOrPostal);
          } else {
            dispatch(
              setZoneZipContext({ selectedZipOrPostal: zipOrPostal, selectedZone: null, overrideZipMatch: true })
            );
          }
        } else {
          if (onOverrideZipOrPostalNO) {
            onOverrideZipOrPostalNO(zipOrPostal);
          } else {
            dispatch(
              setZoneZipContext({
                ...(zoneZipContext || { selectedZipOrPostal: null, selectedZone: null }),
                overrideZipMatch: false
              })
            );
          }
        }
      }
    },
    [
      alertDialog,
      api.companies,
      company.callCenter.serviceAreaNotes,
      company.callCenter.trades,
      company.isZoned,
      company.sisterCompanies,
      confirmationDialog,
      dispatch,
      onCompanySwitchConfirmed,
      onOverrideZipOrPostalNO,
      onOverrideZipOrPostalYES,
      selectSisterCompanyDialog,
      setSelectedOption,
      sisterCompanyContext?.sisterCompanies,
      zoneZipContext
    ]
  );

  const handleSearchChange = useCallback(
    (search: string, options?: { isExternalZipChange?: boolean }) => {
      if (company.isZoned) {
        const exactMatch = search && findZoneZipMatch(search, zoneZipOptions, company.isZoned);
        setSelectedOption(exactMatch || null);
        if (!exactMatch && options?.isExternalZipChange) handleOutOfServiceAreaZip(search);

        // TODO - do we want this behavior for zones?! checked prod and commit history, doesn't look like we ever supported this...
        // if we don't have a match and search is a zip code, see if checkServiceArea finds something in a sister co
        // NOTE - if we decide to go with it, we might be able to set it up such that it only runs on external zip/zone change
        // if (!match && search && ZIP_CODE_REGEX.test(search)) {
        //   handleOutOfServiceAreaZip(search);
        // }
      } else {
        const isValid = !!search && ZIP_CODE_REGEX.test(search);
        setZoneZipSelectState((state) => {
          state.searchError = !isValid;
        });
        if (isValid) {
          const exactMatch = findZoneZipMatch(search, zoneZipOptions, company.isZoned);
          if (exactMatch) {
            setSelectedOption(exactMatch);
          } else {
            handleOutOfServiceAreaZip(search);
          }
        } else if (options?.isExternalZipChange) handleOutOfServiceAreaZip(search);
      }
    },
    [company.isZoned, handleOutOfServiceAreaZip, setZoneZipSelectState, setSelectedOption, zoneZipOptions]
  );

  useEffect(() => {
    if (company.isZoned) {
      setZoneZipSelectState((state) => {
        state.label = zoneZipContext?.selectedZone
          ? `Selected Zone (${zoneZipContext?.selectedZone.title})`
          : 'Selected Zone';
        state.labelHighlight = zoneZipContext?.selectedZone ? '' : '(No Zone Selected)';
      });
    } else {
      setZoneZipSelectState((state) => {
        state.label = 'Selected Zip Code';
        state.labelHighlight = zoneZipContext?.selectedZipOrPostal ? '' : '(No Zip Code Selected)';
      });
    }
  }, [company.isZoned, setZoneZipSelectState, zoneZipContext?.selectedZipOrPostal, zoneZipContext?.selectedZone]);

  /* On remote (i.e. not this component triggering) zip change -> trigger search via new zip */
  useEffect(() => {
    if (externalZipOrPostalRef.current === externalZipOrPostal) return;
    logDebug('// Zip Search triggered - external zipOrPostal change //');

    externalZipOrPostalRef.current = externalZipOrPostal;

    // Only taking the first 5 characters from the external zip since our Companies only support 5-digit US zips
    const externalZipSearch = (externalZipOrPostal || '').substring(0, 5);
    setZoneZipSelectState((state) => {
      state.search = externalZipSearch;
    });

    if (!externalZipSearch) {
      setZoneZipSelectState((state) => {
        state.selectedOption = null;
      });
    } else {
      handleSearchChange(externalZipSearch, { isExternalZipChange: true });
    }
  }, [externalZipOrPostal, handleSearchChange, setZoneZipSelectState]);

  /* On company change -> trigger search with whatever our current search value is (manual or external zip/zone) */
  useEffect(() => {
    if (companyIdRef.current === company.id) return;
    logDebug('// Zip Search triggered - companyId change //');

    companyIdRef.current = company.id;

    if (externalZipOrPostalRef.current) {
      // Only taking the first 5 characters from the external zip since our Companies only support 5-digit US zips
      const externalZipSearch = externalZipOrPostalRef.current.substring(0, 5);
      setZoneZipSelectState((state) => {
        state.search = externalZipSearch;
      });
      handleSearchChange(externalZipSearch);
    } else {
      handleSearchChange(zoneZipSelectState.search);
    }
  }, [company.id, handleSearchChange, setZoneZipSelectState, zoneZipSelectState.search]);

  return (
    <Box>
      <Typography
        variant='body1Bold'
        sx={{ display: 'inline', textTransform: 'none' }}
        text={zoneZipSelectState.label}
      />
      &nbsp;
      <Typography
        variant='body1Bold'
        sx={(theme) => ({ display: 'inline', textTransform: 'none', color: theme.palette.secondary.main })}
        text={zoneZipSelectState.labelHighlight}
      />
      <Autocomplete
        disabled={zoneZipSelectState.disabled}
        filterOptions={zoneZipFilterOptions}
        freeSolo={true}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.zoneTitle || option.zipCode)}
        inputMode='search'
        inputValue={zoneZipSelectState.search}
        isOptionEqualToValue={(option, value) =>
          (option.zoneTitle && option.zoneTitle === value.zoneTitle) || option.zipCode === value.zipCode
        }
        onChange={(_e, value, reason) => {
          if (reason === 'selectOption' && value && typeof value !== 'string') {
            setSelectedOption(value);
          } else if (reason === 'clear') {
            setSelectedOption(null);
          }
        }}
        onInputChange={(_e, searchText, _reason) => {
          setZoneZipSelectState((state) => {
            state.search = searchText;
          });
          handleSearchChange(searchText);
        }}
        open={zoneZipSelectState.searchResultsOpen}
        onOpen={() =>
          setZoneZipSelectState((state) => {
            state.searchResultsOpen = true;
          })
        }
        onClose={() =>
          setZoneZipSelectState((state) => {
            state.searchResultsOpen = false;
          })
        }
        options={zoneZipOptions}
        size='small'
        value={zoneZipSelectState.selectedOption}
        renderInput={(params) => (
          <Tooltip followCursor={true} title={zoneZipSelectState.disabled ? zoneZipSelectState.disabledText : ''}>
            {/* Note - error is only possible when search for non zoned companies since we allow search zone by zone.title or zip code */}
            <TextField
              {...params}
              sx={(theme) => ({
                borderRadius: 0,
                backgroundColor: theme.palette.background.default,
                '& > div': {
                  borderRadius: 0
                },
                '& > input': {
                  backgroundColor: theme.palette.background.default,
                  color: theme.palette.text.primary
                },
                '& > label': {
                  color: theme.palette.text.primary
                }
              })}
              error={!company.isZoned && zoneZipSelectState.searchError}
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  // if Company is NOT Zoned, maxLength is 5 characters
                  maxLength: !company.isZoned ? 5 : undefined
                }
              }}
            />
          </Tooltip>
        )}
        renderOption={(props, option: AutoSelectOption) => (
          <li {...props}>
            {option.zoneTitle && <Typography variant='body1Bold' text={option.zoneTitle} />}
            {!option.zoneTitle && <Typography text={option.zipCode} />}
            {!option.zoneTitle && option.zone && (
              <>
                &nbsp;
                <Typography variant='body1Bold' text={option.zone.title} />
              </>
            )}
          </li>
        )}
      />
      {alertDialog.element}
      {confirmationDialog.element}
      {selectSisterCompanyDialog.element}
    </Box>
  );
};
